import { GetCenterDetailResponse } from '@/api/type/types';
import { fonts } from '@/theme/fonts';
import { formatDate } from '@/utils/date-time/formatDate';
import { getCountryNameByCode } from '@/utils/name/getCountryName';
import { formatInternationalNumber } from '@/utils/number/formatInternationalNumber';
import styled from 'styled-components';
import BusinessFileList from './BusinessFileList';
import DeleteUserModal from '@/components/modal/DeleteUserModal';
import { useCallback, useState } from 'react';
import { deleteCetner } from '@/api/partner-list/center/deleteCetner';
import { useDispatch } from 'react-redux';
import { partner } from '@/stores/slices/partner-list/partnerSlice';
import { useTranslation } from 'react-i18next';

interface PlayerDetailProps {
  detail: GetCenterDetailResponse;
}
const CenterDetail = ({ detail }: PlayerDetailProps) => {
  const [deleteCenterId, setDeletedCenterId] = useState<string>('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const onHandleDelete = useCallback(
    (playerId: string) => () => {
      setDeletedCenterId(playerId);
      setIsDeleteModalOpen(true);
    },
    []
  );

  const onConfirmDelete = useCallback(
    async (remark: string) => {
      const ERROR_MESSAGE: Record<string, string> = {
        E_MEMBER_004: t('deleteAccount.notFound'),
        E_WITHDRAW_001: t('deleteAccount.hasCloudOrderData'),
        E_WITHDRAW_002: t('deleteAccount.hasScannerData'),
        E_WITHDRAW_003: t('deleteAccount.hasChatData'),
        E_WITHDRAW_004: t('deleteAccount.hasPingPongOrderData'),
        E_WITHDRAW_005: t('deleteAccount.hasPartnerServiceData'),
        E_WITHDRAW_006: t('deleteAccount.hasFavouritePlayers'),
        E_WITHDRAW_007: t('deleteAccount.hasPartnerProfileData'),
      };

      try {
        const res = await deleteCetner({
          centerId: deleteCenterId,
          remark: remark,
        });
        if (res.isSuccess) {
          alert(`${detail.loginId} ${t('deleteAccount.deleteSuccess')}`);
          dispatch(partner.removeCenter(res.data.memberId));
          setIsDeleteModalOpen(false);
          return;
        }
        alert(ERROR_MESSAGE[res.error.code] ?? t('deleteAccount.deleteFailed'));
      } catch (e) {
        console.error(e);
        alert(t('deleteAccount.deleteFailed'));
      }
    },
    [deleteCenterId, detail.loginId, dispatch, t]
  );

  const onCloseModal = useCallback(() => {
    setIsDeleteModalOpen(false);
  }, []);

  return (
    <Wrapper>
      <Table>
        <tbody>
          <tr>
            <LabelTd>ID</LabelTd>
            <ValueTd>
              <DataWrap>{detail.loginId}</DataWrap>
            </ValueTd>
          </tr>
          <tr>
            <LabelTd>
              {`${t('common.firstName')} / ${t('common.lastName')}`}
            </LabelTd>
            <ValueTd>
              <DataWrap>
                {`${detail.name.firstName} / ${detail.name.lastName}`}
              </DataWrap>
            </ValueTd>
          </tr>
          <tr>
            <LabelTd>{t('common.representativeName')}</LabelTd>
            <ValueTd>
              <DataWrap>{detail.centerHeadName}</DataWrap>
            </ValueTd>
          </tr>
          <tr>
            <LabelTd>{t('common.dateOfBirth')}</LabelTd>
            <ValueTd>
              <DataWrap>
                {formatDate({ dateString: detail.birthDate })}
              </DataWrap>
            </ValueTd>
          </tr>
          <tr>
            <LabelTd>{t('common.gender')}</LabelTd>
            <ValueTd>
              <DataWrap>
                {detail.gender === 'MALE'
                  ? t('common.male')
                  : t('common.female')}
              </DataWrap>
            </ValueTd>
          </tr>
          <tr>
            <LabelTd>{t('common.nationality')}</LabelTd>
            <ValueTd>
              <DataWrap>
                {getCountryNameByCode({
                  contactCountryCode: detail.contact.contactCountryCode,
                  countryCode: detail.contact.countryCode,
                })}
              </DataWrap>
            </ValueTd>
          </tr>
          <tr>
            <LabelTd>{t('common.mobileNumber')}</LabelTd>
            <ValueTd>
              <DataWrap>
                {formatInternationalNumber({
                  countryCode: detail.contact.contactCountryCode,
                  number: detail.contact.contactNumber,
                })}
              </DataWrap>
            </ValueTd>
          </tr>
          <tr>
            <LabelTd>{t('centerList.centerNumber')}</LabelTd>
            <ValueTd>
              <DataWrap>
                {formatInternationalNumber({
                  countryCode: detail.contact.contactCountryCode,
                  number: detail.centerContactNumber,
                })}
              </DataWrap>
            </ValueTd>
          </tr>
          <tr>
            <LabelTd>{t('centerList.centerName')}</LabelTd>
            <ValueTd>
              <DataWrap>{detail.centerName}</DataWrap>
            </ValueTd>
          </tr>
          <tr>
            <LabelTd>{t('centerList.centerAddress')}</LabelTd>
            {detail.contact.contactCountryCode === '+82' && (
              <ValueTd>
                <AddressSection>
                  <DataWrap>{detail.address.postalCode}</DataWrap>
                  <DataWrap>{detail.address.address1}</DataWrap>
                  <DataWrap>{detail.address.address2}</DataWrap>
                  <DataWrap>{detail.address.city ?? ''}</DataWrap>
                  <DataWrap>{detail.address.state ?? ''}</DataWrap>
                </AddressSection>
              </ValueTd>
            )}
            {detail.contact.contactCountryCode !== '+82' && (
              <ValueTd>
                <AddressSection>
                  <DataWrap>{detail.address.address1}</DataWrap>
                  <DataWrap>{detail.address.address2}</DataWrap>
                  <DataWrap>{detail.address.city ?? ''}</DataWrap>
                  <DataWrap>{detail.address.state ?? ''}</DataWrap>
                  <DataWrap>{detail.address.postalCode ?? ''}</DataWrap>
                </AddressSection>
              </ValueTd>
            )}
          </tr>
          <tr>
            <LabelTd>{t('centerList.businessDocument')}</LabelTd>
            <ValueTd>
              <FileSection>
                <BusinessFileList fileList={detail.businessDocuments} />
              </FileSection>
            </ValueTd>
          </tr>
        </tbody>
      </Table>
      <ButtonSection>
        <DeleteButton onClick={onHandleDelete(detail.id)}>
          {t('deleteAccount.deleteAccount')}
        </DeleteButton>
      </ButtonSection>
      <DeleteUserModal
        open={isDeleteModalOpen}
        onClose={onCloseModal}
        onConfirm={onConfirmDelete}
        role="CENTER"
      />
    </Wrapper>
  );
};

export default CenterDetail;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  background: #ffffff;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 8px;
`;

const LabelTd = styled.td`
  width: 140px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
`;

const ValueTd = styled.td`
  padding: 8px 16px;
  font-size: 14px;
  color: #374151;
`;

const DataWrap = styled.div`
  min-height: 32px;
  padding: 6px 12px;
  display: flex;
  align-items: center;
  border-radius: 6px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  color: #374151;
  font-size: 14px;
`;

const AddressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ButtonSection = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px;
`;

const DeleteButton = styled.button`
  width: 140px;
  height: 36px;
  border-radius: 6px;
  background: #ef4444;
  color: white;
  font-size: 14px;
  font-weight: ${fonts.weight.semiBold};

  &:hover {
    background: #dc2626;
  }
`;

const FileSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
